import * as eff from 'redux-saga/effects';
import { channel } from 'redux-saga';
import { toast } from 'react-toastify';
import i18n from 'i18next';
import MobileDetect from 'mobile-detect';
import mixpanel from 'mixpanel-browser';

import config from 'web-config';
import router from 'web-router';
import { api, coreSagas, coreActions, User } from '@gymcloud/core';
import { fixVHUnit, getNamespaceSymbol } from 'web-utils';
import { uiActions } from 'web-actions';
import { initialApi, analyticsIdentify, trackPageView } from './utils';
import { initialRedirect } from './initialRedirect';
import cableFlow from './cable';

// Types
interface LoginMessage {
  user: User;
  accessToken: string;
  redirectTo?: string;
  payment_required?: boolean;
}

interface WebChannelMessage {
  messageType: string;
  message: any;
}

interface NotifyMessage {
  type?: 'success' | 'error' | 'info' | 'warning' | 'dismiss';
  body?: string;
  key?: string;
  params?: any;
  settings?: any;
}

// Constants
const CORE_FLOWS = [
  coreSagas.announcementFlow,
  coreSagas.authFlow,
  coreSagas.entityFlow,
  coreSagas.productsFlow,
  coreSagas.paymentsFlow,
  coreSagas.documentFlow,
  coreSagas.videoFlow,
  coreSagas.groupFlow,
  coreSagas.headerFlow,
  coreSagas.clientFlow,
  coreSagas.workoutFlow,
  coreSagas.programFlow,
  coreSagas.exerciseFlow,
  coreSagas.conversationFlow,
  coreSagas.workoutEventFlow,
  coreSagas.personalWorkoutFlow,
  coreSagas.clientNotesFlow,
];

const MESSAGE_TYPES = {
  ACCESS_TOKEN_EXIST: 'accessTokenExist',
  LOGIN_SUCCESS: 'loginSuccess',
  FETCH_PROFILE_SUCCESS: 'fetchProfileSuccess',
  RELOAD_PROFILE: 'reloadProfile',
  NOTIFY: 'notify',
  LOGOUT_REQUEST: 'logoutRequest',
} as const;

// Initialize global state
const md = new MobileDetect(window.navigator.userAgent);
const namespaceSymbol = getNamespaceSymbol() || 'gymcloud';

// CSS3 100vh fix for mobile browsers
fixVHUnit();

// Track page changes
router.subscribe(trackPageView);

// Utility functions
function* redirectToApp(message: LoginMessage): Generator<any, void, any> {
  try {
    const namespace = yield eff.call(api.getAppNamespace, {
      symbol: message.user.namespace,
    });

    const redirectUrl = namespace.app_url
      ? `${namespace.app_url.replace(/\/$/, '')}/accessToken/${message.accessToken}`
      : `https://${namespace.symbol}.${window.location.host}/accessToken/${message.accessToken}`;

    window.location.href = redirectUrl;
  } catch (error) {
    console.error('Failed to redirect to app:', error);
  }
}

function* initializeMixpanel(): Generator<any, void, any> {
  if (!config.mixpanel.token) return;

  mixpanel.init(config.mixpanel.token, {
    debug: process.env.TARGET_ENV !== 'production',
    persistence: 'localStorage',
  });

  mixpanel.register({
    'User Namespace': namespaceSymbol,
  });
}

function* initializeNamespace(): Generator<any, void, any> {
  try {
    const namespace = yield eff.call(api.getAppNamespace, {
      symbol: namespaceSymbol,
    });

    i18n.setDefaultNamespace(namespace.symbol);

    yield eff.put(uiActions.setUiState({
      namespace,
      namespaceIsLoaded: true,
    }));
  } catch (error) {
    console.error('Failed to initialize namespace:', error);
  }
}

function* coreFlows(webChannel: any): Generator<any, void, any> {
  yield eff.all(
    CORE_FLOWS.map(flow => eff.fork(flow, webChannel))
  );
}

function* checkExistingAuth(webChannel: any): Generator<any, boolean, any> {
  const [userId, accessToken] = yield eff.all([
    eff.call([localStorage, 'getItem'], 'userId'),
    eff.call([localStorage, 'getItem'], 'accessToken'),
  ]);

  const hasToken = !!(userId && accessToken);

  if (hasToken) {
    yield eff.put(webChannel, {
      messageType: MESSAGE_TYPES.ACCESS_TOKEN_EXIST,
      message: { userId, accessToken },
    });
  }

  yield eff.put(coreActions.setProfileState({ hasToken }));

  return hasToken;
}

// Message handlers
function* handleAccessTokenExist(message: any, webChannel: any): Generator<any, any, any> {
  api.setAccessToken(message.accessToken);
  api.setAdditionalParams({ userId: message.userId });

  return yield eff.fork(coreSagas.profileFlow, webChannel);
}

function* handleLoginSuccess(message: LoginMessage, webChannel: any): Generator<any, any, any> {
  // Check if should redirect to different app
  if (
    window.location.host === 'app.gymcloud.com' &&
    !message.user.namespace.startsWith('namespace_') &&
    message.user.namespace !== 'gymcloud'
  ) {
    yield redirectToApp(message);
    return null;
  }

  const profileTask = yield eff.fork(coreSagas.profileFlow, webChannel);

  yield eff.put(coreActions.setProfileState({ hasToken: true }));

  // Store auth data
  localStorage.setItem('userId', String(message.user.id));
  localStorage.setItem('accessToken', message.accessToken);

  // Handle navigation
  if (md.mobile()) {
    router.navigate(namespaceSymbol === 'strongfirst' ? '/subscription' : '/get_app');
  } else if (!message.payment_required) {
    router.navigate(message.redirectTo || '/welcome');
  }

  return profileTask;
}

function* handleFetchProfileSuccess(
  message: User,
  webChannel: any,
  initializeTask: any
): Generator<any, { initializeTask: any; cableTask: any }, any> {
  // Cancel existing initialization task
  if (initializeTask) {
    yield eff.cancel(initializeTask);
  }

  // Start appropriate resource initialization
  const newInitializeTask = yield eff.fork(
    message.is_pro
      ? coreSagas.initialProResources
      : coreSagas.initialClientResources,
    webChannel
  );

  // Start cable connection
  const cableTask = yield eff.fork(cableFlow, { user: message });

  // Identify user for analytics
  analyticsIdentify(message);

  return { initializeTask: newInitializeTask, cableTask };
}

function* handleReloadProfile(
  webChannel: any,
  profileTask: any
): Generator<any, any, any> {
  if (profileTask) {
    yield eff.cancel(profileTask);
  }

  return yield eff.fork(coreSagas.profileFlow, webChannel);
}

function* handleNotification(message: NotifyMessage): Generator<any, void, any> {
  const toastFunction = message.type && message.type !== 'dismiss'
    ? toast[message.type]
    : toast;

  if (message.type === 'dismiss') {
    toastFunction.dismiss();
  } else if (message.body) {
    toastFunction(message.body, message.settings);
  } else if (message.key) {
    const body = i18n.t(message.key, message.params);
    toastFunction(body || '', message.settings);
  }
}

function* handleLogout(cableTask: any, profileTask: any): Generator<any, void, any> {
  router.navigate('/login');

  // Clear API state
  api.setAccessToken(null);
  api.clearAdditionalParams();

  // Clear local storage
  localStorage.removeItem('userId');
  localStorage.removeItem('accessToken');

  // Reset analytics
  mixpanel.reset();

  // Cancel running tasks
  if (cableTask) {
    yield eff.cancel(cableTask);
  }

  if (profileTask) {
    yield eff.cancel(profileTask);
  }

  yield eff.put({ type: 'LOG_OUT' });
}

// Main saga
export default function* initialize(): Generator<any, void, any> {
  // Initialize API and create channel
  initialApi();
  const webChannel = yield eff.call(channel);

  // Start core processes
  yield eff.fork(initializeNamespace);
  yield eff.fork(coreFlows, webChannel);
  yield eff.fork(initializeMixpanel);

  // Check for existing authentication
  const hasToken = yield* checkExistingAuth(webChannel);
  initialRedirect(hasToken);

  // Task tracking
  let cableTask: any = null;
  let profileTask: any = null;
  let initializeTask: any = null;

  // Main message loop
  while (true) {
    const { messageType, message }: WebChannelMessage = yield eff.take(webChannel);

    try {
      switch (messageType) {
        case MESSAGE_TYPES.ACCESS_TOKEN_EXIST: {
          profileTask = yield* handleAccessTokenExist(message, webChannel);
          break;
        }

        case MESSAGE_TYPES.LOGIN_SUCCESS: {
          const newProfileTask = yield* handleLoginSuccess(message, webChannel);
          if (newProfileTask) {
            profileTask = newProfileTask;
          }
          break;
        }

        case MESSAGE_TYPES.FETCH_PROFILE_SUCCESS: {
          const { initializeTask: newInitTask, cableTask: newCableTask } =
            yield* handleFetchProfileSuccess(message, webChannel, initializeTask);
          initializeTask = newInitTask;
          cableTask = newCableTask;
          break;
        }

        case MESSAGE_TYPES.RELOAD_PROFILE: {
          profileTask = yield* handleReloadProfile(webChannel, profileTask);
          break;
        }

        case MESSAGE_TYPES.NOTIFY: {
          yield* handleNotification(message);
          break;
        }

        case MESSAGE_TYPES.LOGOUT_REQUEST: {
          yield* handleLogout(cableTask, profileTask);
          // Reset task references after logout
          cableTask = null;
          profileTask = null;
          initializeTask = null;
          break;
        }

        default:
          console.warn(`Unhandled message type: ${messageType}`);
          break;
      }
    } catch (error) {
      console.error(`Error handling message type ${messageType}:`, error);
      // Continue processing other messages even if one fails
    }
  }
}
